/**
 * Finance Activity — owner-facing history over finance_events + movements.
 */

const EVENT_KIND_LABELS = Object.freeze({
  manual_inbound: "Money In",
  manual_outbound: "Money Out",
  transfer: "Transfer",
  invoice_payment: "Money In",
  expense_payment: "Money Out",
  payroll_disbursement: "Money Out",
  interest: "Money In",
  fee: "Money Out",
  refund: "Money In",
  adjustment: "Adjustment",
  bank_import_line: "Bank Activity",
  opening_balance_seed: "Opening Balance",
});

export function activityKindLabel(eventType, { flow = null, isReversal = false } = {}) {
  if (isReversal || (eventType === "invoice_payment" && flow === "outbound")) {
    return "Money Out";
  }
  if (eventType === "invoice_payment" && flow === "inbound") {
    return "Money In";
  }
  return EVENT_KIND_LABELS[eventType] || "Activity";
}

export function summarizeActivityRow(event) {
  const movements = Array.isArray(event?.finance_movements)
    ? event.finance_movements
    : Array.isArray(event?.movements)
      ? event.movements
      : [];

  const isReversal =
    !!event?.reversal_of_event_id ||
    event?.metadata?.action === "payment_reversal";
  const primaryFlow =
    movements.find((m) => m.flow === "outbound")?.flow ||
    movements.find((m) => m.flow === "inbound")?.flow ||
    null;
  const kind = activityKindLabel(event?.event_type, {
    flow: primaryFlow,
    isReversal,
  });
  let amount = 0;
  let currency = "CAD";
  const accountNames = [];

  if (event?.event_type === "transfer" && movements.length >= 2) {
    const outbound = movements.find((m) => m.flow === "outbound");
    const inbound = movements.find((m) => m.flow === "inbound");
    amount = Number(outbound?.amount ?? inbound?.amount ?? 0) || 0;
    currency = outbound?.currency || inbound?.currency || "CAD";
    const fromName =
      outbound?.finance_accounts?.name || outbound?.account_name || "Account";
    const toName =
      inbound?.finance_accounts?.name || inbound?.account_name || "Account";
    accountNames.push(`${fromName} → ${toName}`);
  } else {
    for (const movement of movements) {
      amount += Number(movement.amount) || 0;
      currency = movement.currency || currency;
      const name =
        movement.finance_accounts?.name || movement.account_name || null;
      if (name && !accountNames.includes(name)) {
        accountNames.push(name);
      }
    }
  }

  return {
    id: event.id,
    kind,
    eventType: event.event_type,
    occurredOn: event.occurred_on,
    description: event.description || kind,
    notes: event.notes || null,
    amount,
    currency,
    accountSummary: accountNames.join(", ") || "—",
    status: event.status,
  };
}

/**
 * Load recent posted activity (events with nested movements + account names).
 */
export async function listFinanceActivity(client, { limit = 100 } = {}) {
  if (!client) {
    throw new Error("Supabase client is required.");
  }

  const { data, error } = await client
    .from("finance_events")
    .select(
      `
      id,
      event_type,
      status,
      occurred_on,
      description,
      notes,
      created_at,
      reversal_of_event_id,
      metadata,
      finance_movements (
        id,
        flow,
        amount,
        currency,
        account_id,
        finance_accounts ( id, name )
      )
    `,
    )
    .neq("status", "voided")
    .order("occurred_on", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  return (data || []).map(summarizeActivityRow);
}

/**
 * Load active finance categories for optional labeling.
 */
export async function listFinanceCategories(client) {
  if (!client) {
    throw new Error("Supabase client is required.");
  }

  const { data, error } = await client
    .from("finance_categories")
    .select("id, name, type, active")
    .eq("active", true)
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }
  return data || [];
}

export function newFinanceIdempotencyKey(prefix = "ui") {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
